import { automationsDisplayName } from 'corso-types';
import {
  Action,
  Badge,
  Card,
  cn,
  DragAndDropGrip,
  DragAndDropSortableList,
  Icon,
  SupportedIcon,
} from 'corso-admin-ui';
import { useCallback, useEffect, useMemo } from 'react';
import { To, useLocation } from 'react-router-dom';
import { useAutomationUpsert } from '~/features/settings/hooks/useAutomations';
import { Automation } from '~/types';
import AutomationActions from './actions/AutomationActions';

// ? might want to consider a context or something to help with some of the prop drilling

function AutomationRuleListItem({
  automation,
  isDragging,
  isPending,
  draggable,
}: {
  automation: Automation;
  isDragging: boolean;
  isPending: boolean;
  draggable: boolean;
}) {
  const location = useLocation();
  const id = `store-rule-${automation.id}`;
  const isSelected = location.hash === `#${id}`;
  return (
    <div
      id={`store-rule-${automation.id}`}
      className={cn(
        'rounded-md border bg-white p-3 shadow-xs transition-all duration-200 ease-in-out',
        isSelected && 'ring-2 ring-corso-blue-400', // TODO determine why `target` psuedo-class selectors aren't working
        isDragging && 'shadow-lg',
      )}
    >
      <div className="flex flex-col gap-4 md:grid md:grid-cols-[auto_1fr_auto] md:items-center">
        <div className="flex flex-wrap items-center gap-2 rounded-md p-2 transition-colors duration-200 ease-in-out hover:text-neutral-700 focus:ring-offset-2 focus:outline-hidden">
          {draggable && (
            <>
              <DragAndDropGrip item={automation} />
              <Badge
                className={cn(
                  'tabular-nums',
                  isPending && 'motion-safe:animate-pulse',
                )}
              >
                {automation.rule.priority}
              </Badge>
            </>
          )}
          <div className="flex flex-col items-start gap-1">
            <p className="truncate text-left text-sm font-medium text-balance text-neutral-700">
              {automation.name}
            </p>
            <p className="text-xs text-neutral-500 italic">
              {automationsDisplayName.eventType[automation.rule.event.type]}
            </p>
          </div>
        </div>
        <div className="justify-self-end">
          <AutomationActions automation={automation} />
        </div>
      </div>
    </div>
  );
}

const compareBy = {
  priority: (a, b) => a.rule.priority - b.rule.priority,
  name: (a, b) => a.name.localeCompare(b.name),
} as const satisfies Record<string, (a: Automation, b: Automation) => number>;

/**
 * Lists the given `automations`.
 * Often wrapped within a `Card` at some level, but that's not necessarily required.
 */
export default function AutomationRuleList({
  automations: providedAutomations,
  icon,
  label,
  description,
  createTo,
  emptyText = 'No automations.',
  draggable = false,
  sortBy = 'priority',
}: {
  automations: Automation[];
  icon?: SupportedIcon;
  label: string;
  description?: string;
  createTo?: To;
  emptyText?: string;
  draggable?: boolean;
  sortBy?: keyof typeof compareBy;
}) {
  const { mutate: upsertAutomation, isPending } = useAutomationUpsert();

  const sortedAutomations = useMemo(
    () => [...providedAutomations].sort(compareBy[sortBy]),
    [providedAutomations, sortBy],
  );

  const handlePriorityReorder = useCallback(
    (reordered: Automation[]) => {
      reordered.map((automation, index) => {
        if (automation.rule.priority === index + 1) return automation; // no change

        const update = {
          ...automation,
          rule: {
            ...automation.rule,
            priority: index + 1,
          },
        } satisfies Automation;
        upsertAutomation(update);
        return update;
      });
    },
    [upsertAutomation],
  );

  useEffect(() => {
    if (draggable && sortBy !== 'priority') {
      console.warn(
        'AutomationRuleList: `draggable` is true, but `sortBy` is not "priority". Order will not be persisted without an `onReorder` implementation.',
      );
    }
  }, [draggable, sortBy]);

  return (
    // generally wrapped in a `Card`, but controlling the whitespace between elements here
    <div className="space-y-2">
      <Card.Line as="header">
        <div className="flex items-center gap-2">
          {icon && <Icon icon={icon} className="size-6 text-neutral-500" />}
          <Card.Heading>{label}</Card.Heading>
        </div>
        {createTo && (
          <Action
            icon="add"
            accessibilityLabel={`Create ${label} Automation`}
            variant="ghost"
            to={createTo}
          />
        )}
      </Card.Line>
      {description && <Card.Description>{description}</Card.Description>}
      <div className="pt-2">
        {!sortedAutomations.length ? (
          <p className="text-sm text-neutral-400 italic">{emptyText}</p>
        ) : (
          <DragAndDropSortableList
            items={sortedAutomations}
            onReorder={
              draggable && sortBy === 'priority'
                ? handlePriorityReorder
                : undefined
            }
          >
            {(automation, isDragging) => (
              <AutomationRuleListItem
                automation={automation}
                isDragging={isDragging}
                isPending={isPending}
                draggable={draggable}
              />
            )}
          </DragAndDropSortableList>
        )}
      </div>
    </div>
  );
}
